import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const LOGO = '/dailyreps3.png';
const API_BASE = process.env.REACT_APP_API_URL || '';

export default function ParentInvite() {
  const { token } = useParams();

  const [step, setStep] = useState('loading'); // loading | invalid | already_linked | form | success
  const [data, setData] = useState(null);
  const [isLogin, setIsLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/parent-invite/${token}/validate`)
      .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
      .then(({ ok, data: d }) => {
        if (!ok) {
          setError(d.error || 'Invalid invitation link.');
          setStep('invalid');
        } else if (d.already_linked) {
          setData(d);
          setStep('already_linked');
        } else {
          setData(d);
          setEmail(d.email || '');
          setStep('form');
        }
      })
      .catch(() => {
        setError('Failed to validate invitation link.');
        setStep('invalid');
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isLogin) {
      if (!firstName.trim() || !lastName.trim()) {
        setError('First and last name are required.');
        return;
      }
      if (password.length < 8) {
        setError('Password must be at least 8 characters.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    setLoading(true);
    try {
      const body = {
        mode: isLogin ? 'login' : 'register',
        email,
        password,
      };
      if (!isLogin) {
        body.first_name = firstName;
        body.last_name = lastName;
      }

      body.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await fetch(`${API_BASE}/api/parent-invite/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Something went wrong');

      // Store token and redirect — AuthContext will hydrate from /api/auth/me
      localStorage.setItem('token', result.token);
      window.location.href = '/parent';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (step === 'loading') {
    return (
      <div className="login-page">
        <img src={LOGO} alt="Train Daily" className="login-logo" />
        <div className="loading"><div className="spinner" /></div>
      </div>
    );
  }

  if (step === 'invalid') {
    return (
      <div className="login-page">
        <img src={LOGO} alt="Train Daily" className="login-logo" />
        <h1 className="login-title"><span>Train Daily</span> Parent Invite</h1>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <p style={{ color: '#e74c3c', marginBottom: 16 }}>{error}</p>
          <p style={{ color: '#ccc', fontSize: '0.9rem' }}>
            If your link has expired, ask the coach to update the parent email on the player's roster to resend it.
          </p>
        </div>
      </div>
    );
  }

  if (step === 'already_linked') {
    return (
      <div className="login-page">
        <img src={LOGO} alt="Train Daily" className="login-logo" />
        <h1 className="login-title"><span>Train Daily</span> Parent Invite</h1>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <p style={{ color: '#4caf50', marginBottom: 16, fontSize: '1.1rem', fontWeight: 600 }}>
            {data?.player_name} is already connected to your account.
          </p>
          <a
            href="/parent-login"
            style={{ color: '#fff', textDecoration: 'underline', fontSize: '0.9rem' }}
          >
            Go to Parent Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <img src={LOGO} alt="Train Daily" className="login-logo" />
      <h1 className="login-title">
        <span>Train Daily</span> {isLogin ? 'Sign In' : 'Create Parent Account'}
      </h1>
      <p style={{ color: '#ccc', fontSize: '0.9rem', marginBottom: 16, textAlign: 'center', maxWidth: 400 }}>
        {data?.player_name} has been added to <strong>{data?.team_name}</strong>.
        {isLogin
          ? ' Sign in to connect them to your account.'
          : ' Create a parent account to track their progress and manage their profile.'}
      </p>
      <form className="login-form" onSubmit={handleSubmit}>
        {error && <div className="login-error">{error}</div>}
        {!isLogin && (
          <>
            <input
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            <input
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </>
        )}
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {!isLogin && (
          <input
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        )}
        <button className="btn btn-orange" type="submit" disabled={loading}>
          {loading
            ? (isLogin ? 'Signing in...' : 'Creating Account...')
            : (isLogin ? 'Sign In & Connect Player' : 'Create Account & Connect Player')}
        </button>
      </form>
      <button
        onClick={() => { setIsLogin(!isLogin); setError(''); }}
        style={{ background: 'none', border: 'none', color: '#fff', textDecoration: 'underline', cursor: 'pointer', marginTop: 14, fontSize: '0.9rem' }}
      >
        {isLogin ? "Don't have an account? Create one" : 'Already have an account? Sign In'}
      </button>
    </div>
  );
}
